import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import type { FilaVista, MiAlcance, SuplenteFila, SuplentesDeTienda } from '../types/metas.types';
import {
  certificacionMinima,
  limitesInicioDesarrollo,
  minimoDeAlcance,
  primeraSemanaSinBono,
  problemaCertificacion,
  problemaCertificacionEnVentana,
  problemaFechasSuplente,
  puedeCorregirCertificacion,
} from '../utils/catalogo';
import { instanteMx } from '../utils/formato';
import { fechaCorta, semanaEnFrase } from '../utils/semanas';
import { CLASE_OVERLAY } from './ui/clases';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

/** La persona, con la tienda y la semana de la VISTA en que se abrió el diálogo. */
export interface ObjetivoSuplente {
  fila: FilaVista;
  chain: string;
  num: number;
  tienda: string;
  /** Lunes de la plantilla con que se eligió a la persona (lo pide la API). */
  lunes: string;
}

interface Props {
  objetivo: ObjetivoSuplente | null;
  /**
   * `GET suplentes` de la tienda: los permisos de cada programa (retirar,
   * certificar) y los últimos rechazados. `null` si no llegó: la API vuelve a
   * revisar todo al guardar. Puede ir por detrás de la fila (se pide aparte):
   * lo que no cuadra con ella se trata como «no se sabe».
   */
  suplentes: SuplentesDeTienda | null;
  alcance: Pick<MiAlcance, 'esAdmin' | 'ventana' | 'hoy'>;
  /** BUK no respondió: proponer daría 503 (la llave saldría del ERP). */
  plantillaIncompleta?: boolean;
  onHide: () => void;
  /**
   * El programa de la persona cambió. `suplente` es lo que queda (null =
   * ninguno abierto); `datos` es la respuesta de la API cuando trae la tienda.
   */
  onCambio: (key: string, suplente: SuplenteFila | null, datos?: SuplentesDeTienda) => void;
  /** 409: el mensaje ya se ve en el diálogo; quien lo abrió refresca lo suyo (el programa pudo cambiar). */
  onConflicto: (mensaje: string) => void;
  onAviso: AvisoToast;
}

/**
 * Suplente en desarrollo de UNA persona (fase 3): proponerlo, retirar la
 * propuesta o, ya autorizado, registrar su certificación.
 *
 * El bono de suplente es por persona, hasta que se certifica y como máximo 3
 * meses. El supervisor PROPONE y el regional AUTORIZA; la certificación la
 * registra el supervisor y termina el bono.
 *
 * ⚠️ Proponer otra vez REEMPLAZA la propuesta: la anterior queda «retirada» y
 * la nueva trae otro id. Los avisos de la API (gerente o cajero, sin agente en
 * el ERP, reemplazo) se muestran AQUÍ y se cierra a mano: dicen que el bono se
 * registra pero no se va a cobrar, y un aviso que se va solo no se lee.
 */
export const SuplenteDialog: React.FC<Props> = ({
  objetivo,
  suplentes,
  alcance,
  plantillaIncompleta,
  onHide,
  onCambio,
  onConflicto,
  onAviso,
}) => {
  const [inicio, setInicio] = useState('');
  const [certificado, setCertificado] = useState('');
  const [trabajando, setTrabajando] = useState<null | 'proponer' | 'retirar' | 'certificar'>(null);
  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[] | null>(null);
  const pedido = useRef(0);

  const fila = objetivo?.fila ?? null;
  const programa = fila?.suplente ?? null;
  const modo: 'proponer' | 'certificar' = programa?.estado === 'autorizada' ? 'certificar' : 'proponer';

  // Cada vez que se abre para otra persona: lo del programa abierto, si hay.
  useEffect(() => {
    pedido.current++;
    const p = objetivo?.fila.suplente ?? null;
    setInicio(p?.inicioDesarrollo ?? '');
    setCertificado(p?.certificadoEn ?? '');
    setTrabajando(null);
    setError(null);
    setAvisos(null);
  }, [objetivo]);

  if (!objetivo || !fila) return null;

  // Los permisos del programa, sólo si la lista lo tiene con el MISMO id y el
  // MISMO estado que la fila. La fila llega con la vista y la lista aparte:
  // tras guardar, la fila podía traer el id nuevo de una propuesta reemplazada
  // en esta misma tienda, o ya «autorizada», con la lista aún de antes. Se
  // leía como «abierto en otra tienda» o se negaba «Certificar» que la API
  // permitía. Si no cuadra, NO se sabe: se deja actuar y decide la API (su
  // mensaje se ve aquí). Nunca se afirma que el programa es de otra tienda.
  const abierto = programa
    ? suplentes?.abiertos.find((a) => a.id === programa.id && a.estado === programa.estado) ?? null
    : null;
  const rechazado =
    suplentes?.cerrados.find((c) => c.key === fila.key && c.estado === 'rechazada' && !!c.comentario) ?? null;
  const puedeProponer = suplentes ? suplentes.permisos.proponer : true;
  const puedeRetirar = programa?.estado === 'propuesta' && (abierto ? abierto.permisos.retirar : true);
  const puedeCertificar = modo === 'certificar' && (abierto ? abierto.permisos.certificar : true);
  const limites = limitesInicioDesarrollo(alcance);
  // El primer lunes abierto de la cuenta (`null` = administrador): la
  // certificación tampoco puede tocar semanas cerradas.
  const minimo = minimoDeAlcance(alcance);

  const problema =
    modo === 'proponer'
      ? problemaFechasSuplente({ inicio, certificado, min: limites.min, max: limites.max, hoy: alcance.hoy })
      : programa
        ? problemaCertificacion(certificado, programa.inicioDesarrollo, alcance.hoy) ??
          problemaCertificacionEnVentana(certificado, programa.certificadoEn, minimo)
        : null;
  // «Escribe la fecha…» no se pinta en rojo antes de que se escriba nada.
  const problemaVisible = problema && (modo === 'proponer' ? inicio !== '' : certificado !== '') ? problema : null;

  // ⚠️ La API usa 409 para dos cosas: el programa cambió (lo resolvieron, lo
  // retiraron, otra persona lo propuso) y la regla de la ventana (una fecha que
  // tocaría semanas cerradas). No se distinguen sin leer el texto, así que el
  // mensaje se queda AQUÍ, donde se está mirando, y quien abrió el diálogo
  // refresca lo suyo por si fue lo primero.
  const fallo = (e: unknown, n: number) => {
    if (n !== pedido.current) return;
    setError((e as Error).message);
    if (e instanceof MetasApiError && e.esConflicto) onConflicto(e.message);
  };

  const proponer = async () => {
    if (problema) return;
    const n = pedido.current;
    const { chain, num, lunes } = objetivo;
    setTrabajando('proponer');
    setError(null);
    try {
      const r = await MetasColaboradorService.proponerSuplente(chain, num, {
        key: fila.key,
        inicioDesarrollo: inicio,
        certificadoEn: certificado || null,
        lunes,
      });
      if (n !== pedido.current) return;
      // El id es NUEVO: sale de la respuesta, nunca del programa anterior.
      const nuevo = r.abiertos.find((a) => a.key === fila.key && a.estado === 'propuesta');
      onCambio(
        fila.key,
        nuevo
          ? {
              id: nuevo.id,
              estado: 'propuesta',
              inicioDesarrollo: nuevo.inicioDesarrollo,
              certificadoEn: nuevo.certificadoEn,
              // Una propuesta nunca cobra: sólo lo autorizado es vigente.
              vigenteEstaSemana: false,
            }
          : null,
        r,
      );
      const lista = r.avisos ?? [];
      if (lista.length) setAvisos(lista);
      else {
        onAviso('success', 'Suplente propuesto', `${fila.nombre}: lo autoriza el regional.`);
        onHide();
      }
    } catch (e) {
      fallo(e, n);
    } finally {
      if (n === pedido.current) setTrabajando(null);
    }
  };

  const retirar = async () => {
    if (!programa) return;
    const n = pedido.current;
    setTrabajando('retirar');
    setError(null);
    try {
      await MetasColaboradorService.retirarSuplente(programa.id);
      if (n !== pedido.current) return;
      onCambio(fila.key, null);
      onAviso('success', 'Propuesta retirada', `${fila.nombre} ya no está propuesto como suplente.`);
      onHide();
    } catch (e) {
      fallo(e, n);
    } finally {
      if (n === pedido.current) setTrabajando(null);
    }
  };

  const certificar = async () => {
    if (!programa || problema) return;
    const n = pedido.current;
    const { lunes } = objetivo;
    setTrabajando('certificar');
    setError(null);
    try {
      const r = await MetasColaboradorService.certificarSuplente(programa.id, certificado);
      if (n !== pedido.current) return;
      const cert = r.certificadoEn ?? certificado;
      onCambio(fila.key, {
        ...programa,
        certificadoEn: cert,
        // `metas_suplentes_vigentes` lo paga mientras `certificado_en > lunes`:
        // lo demás de la vigencia no cambió.
        vigenteEstaSemana: programa.vigenteEstaSemana && cert > lunes,
      });
      onAviso(
        'success',
        'Certificación registrada',
        `${fila.nombre} deja de cobrar el bono de suplente desde la ${semanaEnFrase(primeraSemanaSinBono(cert))}.`,
      );
      onHide();
    } catch (e) {
      fallo(e, n);
    } finally {
      if (n === pedido.current) setTrabajando(null);
    }
  };

  const ocupado = trabajando !== null;

  // ------------------------------------------------------------ piezas
  const pie = avisos ? (
    <div className="flex justify-end">
      <Button label="Entendido" icon="pi pi-check" onClick={onHide} />
    </div>
  ) : (
    <div className="flex flex-wrap justify-end gap-2">
      {modo === 'proponer' && puedeRetirar && (
        <Button
          label="Retirar propuesta"
          icon="pi pi-trash"
          severity="danger"
          text
          onClick={() => void retirar()}
          loading={trabajando === 'retirar'}
          disabled={ocupado}
        />
      )}
      <Button label="Cancelar" text onClick={onHide} disabled={ocupado} />
      {modo === 'proponer' ? (
        <Button
          label={programa ? 'Reemplazar la propuesta' : 'Proponer al regional'}
          icon="pi pi-send"
          onClick={() => void proponer()}
          loading={trabajando === 'proponer'}
          disabled={ocupado || !!problema || !puedeProponer || !!plantillaIncompleta}
        />
      ) : (
        <Button
          label="Registrar certificación"
          icon="pi pi-verified"
          onClick={() => void certificar()}
          loading={trabajando === 'certificar'}
          disabled={ocupado || !!problema || !puedeCertificar}
        />
      )}
    </div>
  );

  const cuerpoProponer = (
    <>
      <p className="text-sm text-gray-600">
        Bono semanal de suplente mientras se desarrolla: hasta certificarse y como máximo 3 meses. Lo autoriza el
        regional; hasta entonces no se cobra.
      </p>
      {/* El NOMBRE de quien propuso o rechazó, con su correo en el `title`
          (como en CondicionesPanel): dentro del `text` de un Message no cabe
          un elemento con tooltip (F4-V4). */}
      {programa?.estado === 'propuesta' && (
        <Message
          severity="info"
          content={
            <span data-testid="suplente-propuesto">
              Propuesto desde el {fechaCorta(programa.inicioDesarrollo)}
              {abierto && (
                <>
                  {' '}por <span title={abierto.propuestaPor}>{abierto.propuestaPorNombre || abierto.propuestaPor}</span> el{' '}
                  {instanteMx(abierto.propuestaEn)}
                </>
              )}
              : espera al regional. Proponer otra vez la reemplaza y el regional sólo verá la nueva.
            </span>
          }
        />
      )}
      {rechazado && (
        <Message
          severity="warn"
          content={
            <span data-testid="suplente-rechazado">
              El regional rechazó la propuesta anterior
              {(rechazado.resueltoPorNombre || rechazado.resueltoPor) && (
                <>
                  {' '}(<span title={rechazado.resueltoPor ?? undefined}>{rechazado.resueltoPorNombre || rechazado.resueltoPor}</span>)
                </>
              )}
              : «{rechazado.comentario}»
            </span>
          }
        />
      )}
      {!puedeProponer && <Message severity="info" text="Tu cuenta no puede proponer suplentes en esta tienda." />}
      {plantillaIncompleta && (
        <Message
          severity="error"
          text="BUK no respondió: no se puede proponer hasta que vuelva (la persona saldría con la llave del ERP). Intenta en unos minutos."
        />
      )}
      <div className="flex flex-col gap-1">
        <label htmlFor="suplente-inicio" className="font-semibold">
          Inicio del desarrollo
        </label>
        <InputText
          id="suplente-inicio"
          type="date"
          value={inicio}
          min={limites.min ?? undefined}
          max={limites.max ?? undefined}
          onChange={(e) => setInicio(e.target.value)}
          className="w-full"
        />
        {limites.min && (
          <small className="text-gray-600">
            Desde el {fechaCorta(limites.min)}: antes haría cobrar el bono en semanas cerradas (eso lo registra un
            administrador).
          </small>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="suplente-certificado" className="font-semibold">
          Certificación <span className="font-normal text-gray-500">(opcional, si ya ocurrió)</span>
        </label>
        <InputText
          id="suplente-certificado"
          type="date"
          value={certificado}
          min={inicio || undefined}
          max={alcance.hoy}
          onChange={(e) => setCertificado(e.target.value)}
          className="w-full"
        />
      </div>
    </>
  );

  const cuerpoCertificar = programa && (
    <>
      <p className="text-sm text-gray-600" data-testid="suplente-autorizado">
        En desarrollo desde el <strong>{fechaCorta(programa.inicioDesarrollo)}</strong>, autorizado
        {abierto?.resueltoEn && (abierto.resueltoPorNombre || abierto.resueltoPor) && (
          <>
            {' '}por <span title={abierto.resueltoPor ?? undefined}>{abierto.resueltoPorNombre || abierto.resueltoPor}</span> el{' '}
            {instanteMx(abierto.resueltoEn)}
          </>
        )}
        . La certificación termina el bono de suplente.
      </p>
      {/* Si ya cerró semanas, no «se corrige»: lo dice el error de abajo. */}
      {programa.certificadoEn && puedeCorregirCertificacion(programa.certificadoEn, minimo) && (
        <Message
          severity="info"
          text={`Ya tiene certificación del ${fechaCorta(programa.certificadoEn)}: guardar otra fecha la corrige y la anterior queda en la bitácora.`}
        />
      )}
      {!puedeCertificar && (
        <Message severity="info" text="Tu cuenta no puede registrar la certificación de este suplente." />
      )}
      <div className="flex flex-col gap-1">
        <label htmlFor="suplente-certificacion" className="font-semibold">
          Fecha de certificación
        </label>
        <InputText
          id="suplente-certificacion"
          type="date"
          value={certificado}
          min={certificacionMinima(programa.inicioDesarrollo, minimo)}
          max={alcance.hoy}
          onChange={(e) => setCertificado(e.target.value)}
          className="w-full"
        />
        {certificado && !problema && (
          <small className="text-gray-600" data-testid="suplente-ultima-semana">
            Deja de cobrar el bono desde la {semanaEnFrase(primeraSemanaSinBono(certificado))}.
          </small>
        )}
      </div>
    </>
  );

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={`${modo === 'certificar' ? 'Certificar suplente' : 'Suplente en desarrollo'} · ${fila.nombre}`}
      visible
      onHide={() => {
        if (!ocupado) onHide();
      }}
      footer={pie}
      style={{ width: '34rem', maxWidth: '95vw' }}
      modal
    >
      <div className="flex flex-col gap-4" data-testid="suplente-dialogo">
        {avisos ? (
          <>
            <Message severity="success" text={`${fila.nombre} quedó propuesto como suplente: lo autoriza el regional.`} />
            <ul className="flex flex-col gap-2" data-testid="suplente-avisos">
              {avisos.map((a) => (
                <li key={a}>
                  <Message severity="warn" text={a} className="w-full justify-start" />
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            {modo === 'proponer' ? cuerpoProponer : cuerpoCertificar}
            {problemaVisible && <Message severity="error" text={problemaVisible} />}
            {error && <Message severity="error" text={error} />}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default SuplenteDialog;
